/*
    What Lob's renderer can actually do (MPN-1775).

    Every fact here was established by rendering a probe card through Lob's test key and
    reading the artwork that came back, not from documentation. That matters because this
    renderer's failure mode is silence: it returns 200 and a PDF whatever you send it, and
    the mistake turns up as a bad card a week later.

    The engine is older than the browser you are reading this in. Treat it as such.
*/

/**
 * Fonts the renderer has INSTALLED. Naming any other family with no `@font-face` rule falls
 * back to a serif face in silence.
 *
 * Checked one by one. The web-safe set is present and so is DejaVu Sans; the fonts a
 * designer actually reaches for are not -- Roboto, Open Sans, Montserrat, Lato and Inter all
 * came back as serif.
 *
 * This is NOT the whole story, and the first version of this file got it wrong by stopping
 * here. The renderer will happily load a font it does not have if the artwork carries an
 * `@font-face` rule pointing at a real file: Inter served from the platform's own font API
 * renders as Inter, confirmed against a printed proof. So `substituteFont` is the fallback
 * for a family we cannot fetch a face for, not the first move.
 */
export const AVAILABLE_FONTS = [
    "Helvetica", "Arial", "Times New Roman", "Georgia", "Courier New", "Verdana",
    "Tahoma", "Trebuchet MS", "Palatino", "Garamond", "Impact", "Comic Sans MS",
    "DejaVu Sans",
] as const;

/**
 * Where to send a font the renderer does not have.
 *
 * Matched on shape rather than alphabetically: a geometric sans belongs with a sans, and
 * dropping Montserrat into Times is a worse card than dropping it into Helvetica.
 */
const SUBSTITUTIONS: Record<string, string> = {
    // Geometric and neo-grotesque sans -> Helvetica
    roboto: "Helvetica", "open sans": "Helvetica", montserrat: "Helvetica",
    lato: "Helvetica", inter: "Helvetica", poppins: "Helvetica", nunito: "Helvetica",
    "source sans pro": "Helvetica", "noto sans": "Helvetica", raleway: "Helvetica",
    "work sans": "Helvetica", ubuntu: "Helvetica", "liberation sans": "Helvetica",
    "segoe ui": "Helvetica", "system-ui": "Helvetica", "sans-serif": "Helvetica",
    // Humanist sans with wider counters -> Verdana
    "pt sans": "Verdana", "fira sans": "Verdana",
    // Serifs -> Georgia, which is the most robust serif on the box
    merriweather: "Georgia", "playfair display": "Georgia", lora: "Georgia",
    "pt serif": "Georgia", "noto serif": "Georgia", "source serif pro": "Georgia",
    "times": "Times New Roman", serif: "Georgia",
    // Monospace
    "roboto mono": "Courier New", "source code pro": "Courier New",
    "fira code": "Courier New", monospace: "Courier New", menlo: "Courier New",
    consolas: "Courier New",
};

const AVAILABLE_LOWER = new Set(AVAILABLE_FONTS.map((f) => f.toLowerCase()));

export interface FontChoice {
    /** The family to put in the CSS. Always one the renderer has. */
    family: string;
    /** True when the requested family was not available and something else was chosen. */
    substituted: boolean;
    /** What was asked for, kept so the UI can say what happened. */
    requested: string;
}

/**
 * Resolve a font to one the printer actually has.
 *
 * Never returns an unavailable family. A design that asks for something missing is reported
 * as substituted so the operator is told, rather than finding out from the post.
 */
export function substituteFont(requested: string | null | undefined): FontChoice {
    const asked = (requested || "").trim();
    if (!asked) return { family: "Helvetica", substituted: false, requested: "" };

    // A CSS stack: take the first entry the renderer has, which is what a browser would do.
    const stack = asked.split(",").map((f) => f.trim().replace(/^["']|["']$/g, ""));
    for (const family of stack) {
        if (AVAILABLE_LOWER.has(family.toLowerCase())) {
            return { family, substituted: false, requested: asked };
        }
    }

    for (const family of stack) {
        const mapped = SUBSTITUTIONS[family.toLowerCase()];
        if (mapped) return { family: mapped, substituted: true, requested: asked };
    }

    return { family: "Helvetica", substituted: true, requested: asked };
}

/**
 * CSS the renderer ignores, and the damage each one does.
 *
 * Used to check generated and hand-edited artwork before it is sent. `inset` and flexbox are
 * not hypothetical: the first draft of the starter templates used both and Lob returned a
 * blank white card with white text on it (MPN-1774).
 */
export const UNSUPPORTED_CSS: { pattern: RegExp; what: string; consequence: string }[] = [
    {
        pattern: /display:\s*flex/i,
        what: "display: flex",
        consequence: "children are laid out inline and every flex property is ignored",
    },
    {
        pattern: /display:\s*(inline-)?grid/i,
        what: "display: grid",
        consequence: "the grid collapses and children stack in source order",
    },
    {
        pattern: /(^|[;{\s])inset:/i,
        what: "the inset shorthand",
        consequence: "the box is never positioned, so backgrounds disappear entirely",
    },
    {
        // A stylesheet `@import` is fetched too late to style the page being rasterised.
        // Note this deliberately does NOT include `@font-face`, which works: see
        // AVAILABLE_FONTS.
        pattern: /@import|<link[^>]+stylesheet/i,
        what: "an imported stylesheet",
        consequence: "it arrives after the page is drawn, so none of its rules apply",
    },
];

/**
 * Is this family one the printer can render without being sent the font file?
 *
 * Anything false here needs an `@font-face` rule travelling with the artwork, or it prints
 * as a serif with nothing to say why.
 */
export function isInstalledFont(family: string): boolean {
    return AVAILABLE_LOWER.has(family.trim().replace(/^["']|["']$/g, "").toLowerCase());
}

export interface ArtworkWarning {
    what: string;
    consequence: string;
}

/** Everything in this artwork that the printer will quietly ignore. */
export function unsupportedCssIn(html: string): ArtworkWarning[] {
    return UNSUPPORTED_CSS
        .filter(({ pattern }) => pattern.test(html))
        .map(({ what, consequence }) => ({ what, consequence }));
}
